import type { JSX } from 'react';
import { useEffect, useState, type FormEvent } from 'react';
import type { Principal } from '@ampshift/shared';
import { fetchDevPrincipals, isCognitoConfigured, isDevAuthEnabled, signInAsDevPrincipal, signInWithCognito } from '../auth';
import { useT } from '../i18n/useT';

export function LoginScreen({ notice }: { notice?: string }): JSX.Element {
  const t = useT();
  const dev = isDevAuthEnabled();
  const [principals, setPrincipals] = useState<Record<string, Principal> | undefined>();
  const [devError, setDevError] = useState<string | undefined>();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!dev) return;
    let cancelled = false;
    fetchDevPrincipals()
      .then((p) => {
        if (!cancelled) setPrincipals(p);
      })
      .catch((e: unknown) => {
        if (!cancelled) setDevError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [dev]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await signInWithCognito(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login-card">
        <div className="login-brand">
          <img src="/ampshift-logo.svg" alt="AmpShift" width={48} height={48} />
          <div>
            <h1>{t('productName')}</h1>
            <span className="muted">Conversational EMR · Navigation replaced by conversation</span>
          </div>
        </div>
        {notice ? <p className="notice">{notice}</p> : null}
        {dev ? (
          <section className="dev-principals">
            <h2>{t('demoPrincipals')}</h2>
            {devError ? <p className="error-text">{devError}</p> : null}
            {!principals && !devError ? <p className="muted">{t('loadingPrincipals')}</p> : null}
            <div className="principal-list">
              {principals
                ? Object.entries(principals).map(([key, p]) => (
                    <button key={key} type="button" className="btn principal" onClick={() => signInAsDevPrincipal(p)} data-testid={`principal-${key}`}>
                      <strong>{p.displayName ?? key}</strong>
                      <span className="muted small">
                        {p.roles.join(', ')} · {p.tenantId}
                      </span>
                    </button>
                  ))
                : null}
            </div>
          </section>
        ) : null}
        {isCognitoConfigured() || !dev ? (
          <form onSubmit={(e) => void submit(e)} className="login-form">
            <h2>{t('signIn')}</h2>
            <label>
              {t('email')}
              <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label>
              {t('password')}
              <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            {error ? <p className="error-text">{error}</p> : null}
            <button type="submit" className="btn btn-primary" disabled={busy || !isCognitoConfigured()}>
              {busy ? t('signingIn') : t('signIn')}
            </button>
            {!isCognitoConfigured() ? <p className="muted small">Cognito is not configured (VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID).</p> : null}
          </form>
        ) : null}
      </div>
    </div>
  );
}
